import React, { useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, LogIn, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface LoginPromptContextType {
  showLoginPrompt: (action?: string) => void;
  hideLoginPrompt: () => void;
}

const LoginPromptContext = createContext<LoginPromptContextType | undefined>(undefined);

export function useLoginPrompt() {
  const context = useContext(LoginPromptContext);
  if (!context) {
    throw new Error('useLoginPrompt must be used within LoginPromptProvider');
  }
  return context;
}

interface LoginPromptProviderProps {
  children: React.ReactNode;
}

export function LoginPromptProvider({ children }: LoginPromptProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [actionText, setActionText] = useState('interact');
  const navigate = useNavigate();

  const showLoginPrompt = (action = 'interact') => {
    setActionText(action);
    setIsOpen(true);
  };

  const hideLoginPrompt = () => {
    setIsOpen(false);
  };

  const handleSignIn = () => {
    hideLoginPrompt();
    navigate('/login');
  };

  const handleCreateAccount = () => {
    hideLoginPrompt();
    navigate('/login');
  };

  return (
    <LoginPromptContext.Provider value={{ showLoginPrompt, hideLoginPrompt }}>
      {children}
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Sign in to {actionText}</DialogTitle>
            <DialogDescription className="text-center">
              Join the RecruitLook community to connect with athletes, coaches, and scouts.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-3 py-4">
            <Button onClick={handleSignIn} className="w-full" size="lg">
              <LogIn className="h-4 w-4 mr-2" />
              Sign In
            </Button>
            
            <Button onClick={handleCreateAccount} variant="outline" className="w-full" size="lg">
              <UserPlus className="h-4 w-4 mr-2" />
              Create Account
            </Button>
            
            <Button 
              onClick={hideLoginPrompt} 
              variant="ghost" 
              className="w-full text-muted-foreground"
            >
              Continue Browsing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </LoginPromptContext.Provider>
  );
}
